import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { tickerApi } from '../../lib/api';
import { usePostgresChanges } from '../../hooks/useSupabaseRealtime';
import { Radio, ChevronRight, X, Zap } from 'lucide-react';

const LiveTicker = () => {
  const [tickerData, setTickerData] = useState(null);
  const [picks, setPicks] = useState([]);
  const [newPickIds, setNewPickIds] = useState(new Set());
  const [dismissed, setDismissed] = useState(false);
  const [paused, setPaused] = useState(false);
  const scrollRef = useRef(null);

  // Fetch initial ticker data
  useEffect(() => {
    const fetchTicker = async () => {
      try {
        const res = await tickerApi.getPicks(20);
        const data = res.data;
        if (data.picks && data.picks.length > 0) {
          setTickerData(data);
          setPicks(data.picks);
        }
      } catch (e) { /* no ticker data */ }
    };
    fetchTicker();
    const interval = setInterval(fetchTicker, 30000);
    return () => clearInterval(interval);
  }, []);

  // Supabase Realtime: Listen for draft_events updates (new picks)
  usePostgresChanges('draft_events', 'UPDATE', useCallback((payload) => {
    const updated = payload.new;
    if (!tickerData?.event_id || updated.id !== tickerData.event_id) return;
    // Check for new picks
    if (updated.picks && Array.isArray(updated.picks)) {
      const latestPick = updated.picks[updated.picks.length - 1];
      if (latestPick) {
        const newPick = {
          round: latestPick.round,
          pick: latestPick.pick,
          prospect_id: latestPick.prospect_id,
          prospect_name: latestPick.prospect_name,
          position: latestPick.position,
          auto_picked: latestPick.auto_picked,
          picked_at: latestPick.picked_at,
        };
        const pickKey = `${newPick.round}-${newPick.pick}`;
        setPicks(prev => {
          if (prev.some(p => `${p.round}-${p.pick}` === pickKey)) return prev;
          return [newPick, ...prev].slice(0, 20);
        });
        setNewPickIds(prev => new Set(prev).add(pickKey));
        setTimeout(() => {
          setNewPickIds(prev => {
            const next = new Set(prev);
            next.delete(pickKey);
            return next;
          });
        }, 5000);
      }
    }
    if (updated.status === 'completed') {
      setTickerData(prev => prev ? { ...prev, status: 'completed' } : prev);
    }
  }, [tickerData?.event_id]));

  if (!tickerData || picks.length === 0 || dismissed) return null;

  const isLive = tickerData.status === 'live';

  // Duplicate picks for seamless scroll loop
  const displayPicks = [...picks, ...picks];

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 bg-zinc-950 border-t border-zinc-800"
      data-testid="live-ticker"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex items-center h-10">
        {/* Label */}
        <div className="flex items-center gap-2 px-4 bg-zinc-900 h-full border-r border-zinc-800 shrink-0" data-testid="ticker-label">
          {isLive ? (
            <>
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="font-heading font-bold text-xs uppercase tracking-widest text-red-500">Live</span>
            </>
          ) : (
            <>
              <Radio className="w-3.5 h-3.5 text-primary" />
              <span className="font-heading font-bold text-xs uppercase tracking-widest text-primary">Draft</span>
            </>
          )}
        </div>

        {/* Scrolling ticker */}
        <div className="flex-1 overflow-hidden relative" ref={scrollRef}>
          <div
            className={`flex items-center gap-0 ticker-scroll ${paused ? 'ticker-paused' : ''}`}
            style={{ '--ticker-items': picks.length }}
            data-testid="ticker-track"
          >
            {displayPicks.map((pick, i) => {
              const pickKey = `${pick.round}-${pick.pick}`;
              const isNew = newPickIds.has(pickKey);
              return (
                <Link
                  key={`${pickKey}-${i}`}
                  to={`/prospects/${pick.prospect_id}`}
                  className={`flex items-center gap-2 px-4 h-10 shrink-0 transition-colors hover:bg-zinc-800 group ${
                    isNew ? 'ticker-new-pick' : ''
                  }`}
                  data-testid={`ticker-pick-${i}`}
                >
                  {/* Pick badge */}
                  <span className={`font-mono text-[10px] font-bold px-1.5 py-0.5 ${
                    isNew ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    R{pick.round}P{pick.pick}
                  </span>

                  {/* Player info */}
                  <span className="font-heading text-sm uppercase font-semibold text-white group-hover:text-primary transition-colors whitespace-nowrap">
                    {pick.prospect_name}
                  </span>

                  {/* Position */}
                  <span className="font-mono text-[10px] text-zinc-500 uppercase whitespace-nowrap">
                    {pick.position}
                  </span>

                  {/* Auto badge */}
                  {pick.auto_picked && (
                    <Zap className="w-3 h-3 text-yellow-500 shrink-0" />
                  )}

                  {/* Divider */}
                  <span className="text-zinc-700 ml-2">|</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Event link + dismiss */}
        <div className="flex items-center gap-1 px-3 h-full border-l border-zinc-800 shrink-0">
          {tickerData.event_id && (
            <Link
              to={`/draft-day/${tickerData.event_id}`}
              className="flex items-center gap-1 text-xs text-primary hover:text-white transition-colors font-heading uppercase tracking-wider"
              data-testid="ticker-event-link"
            >
              Watch
              <ChevronRight className="w-3 h-3" />
            </Link>
          )}
          <button
            onClick={() => setDismissed(true)}
            className="p-1 text-zinc-600 hover:text-white transition-colors ml-1"
            data-testid="ticker-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LiveTicker;
